import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ApiError } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { formatDateTime } from '@/utils/locale';
import { useDeploymentEnvironment } from '@/hooks/useDeploymentEnvironment';
import {
  approveRelease,
  getChangesSinceProduction,
  getReleases,
  type ReleaseBlockReason,
  type ReleaseHistoryEntry,
  type ReleaseRun,
  type ReleasesState,
} from '@/api/releaseApproval';
import { ApproveReleaseDialog } from './ApproveReleaseDialog';
import { GithubErrorAlert } from './GithubErrorAlert';
import { TestDataCard } from './TestDataCard';
import { ChangesChecklist } from './ChangesChecklist';
import {
  inheritedOnlyChanges,
  notTestedChanges,
  openFindings,
  summarize,
  untestedChanges,
  useTestResults,
} from './testResults';
import { VersionCards } from './VersionCards';

/** Reasons shown to a person who holds the permission; others see who can approve. */
const BLOCK_KEYS = {
  not_configured: 'blocked.notConfigured',
  unknown_candidate: 'blocked.unknownCandidate',
  already_in_production: 'blocked.alreadyInProduction',
  release_in_progress: 'blocked.releaseInProgress',
  restore_stale: 'blocked.restoreStale',
} as const;

/** GitHub problems are shown once at the top of the page (GithubErrorAlert). */
function blockKey(reason: ReleaseBlockReason | null): (typeof BLOCK_KEYS)[keyof typeof BLOCK_KEYS] | null {
  if (reason === null || reason === 'no_permission') return null;
  if (reason === 'github_permission_missing' || reason === 'github_unavailable') return null;
  return BLOCK_KEYS[reason];
}

const HISTORY_STATUS_COLOR = {
  success: 'success',
  failure: 'error',
  in_progress: 'info',
  pending: 'default',
} as const;

/**
 * Only a failed release is announced at the top: it needs action. A running or
 * finished release shows on the version cards (#3481) and in the history; a
 * "published" banner made the page look as if nothing were left to test.
 */
function FailedRunAlert({ run }: { run: ReleaseRun | null }) {
  const { t } = useTranslation('releaseApproval');
  if (run?.status !== 'failure') return null;
  return <Alert severity="error">{t('run.failed')}</Alert>;
}

function HistoryList({ history }: { history: readonly ReleaseHistoryEntry[] }) {
  const { t } = useTranslation('releaseApproval');
  if (history.length === 0) return <Typography color="text.secondary">{t('history.empty')}</Typography>;
  return (
    <List dense disablePadding>
      {history.map((entry) => (
        <ListItem key={`${entry.candidateSha}-${entry.createdAt}`} disableGutters divider>
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {entry.candidateSha.slice(0, 7)}
                </Typography>
                <Chip size="small" label={t(`history.status.${entry.status}`)} color={HISTORY_STATUS_COLOR[entry.status]} />
                {entry.mode !== 'approval' && (
                  <Chip size="small" variant="outlined" color="warning" label={t(`history.mode.${entry.mode}`)} />
                )}
              </Stack>
            }
            secondary={
              <>
                {formatDateTime(entry.createdAt)}
                {entry.approvedBy ? ` · ${t('history.approvedBy', { name: entry.approvedBy })}` : ''}
                {entry.reason ? ` · ${entry.reason}` : ''}
                {entry.comment ? ` · “${entry.comment}”` : ''}
                {entry.findings && (
                  <>
                    {' · '}
                    <span data-testid="history-findings">
                      {t('history.findings', {
                        openNotOk: entry.findings.openNotOk,
                        untested: entry.findings.untested,
                        notTested: entry.findings.notTested ?? 0,
                      })}
                    </span>
                    {entry.findings.resultsIssue && (
                      <>
                        {' · '}
                        <Link href={entry.findings.resultsIssue} target="_blank" rel="noopener noreferrer">
                          {t('history.resultsLink')}
                        </Link>
                      </>
                    )}
                  </>
                )}
              </>
            }
          />
        </ListItem>
      ))}
    </List>
  );
}

/**
 * The customer's view of the test version and its route to production (#3316).
 * Test environments only: the page says so and offers nothing in production.
 */
export function ReleaseApprovalPage() {
  const { t, i18n } = useTranslation('releaseApproval');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { environment, isTestEnvironment } = useDeploymentEnvironment();
  const [dialogOpen, setDialogOpen] = useState(false);

  const releases = useQuery({
    queryKey: ['releases'],
    queryFn: getReleases,
    enabled: isTestEnvironment,
    // Follow a running release closely; otherwise just stay fresh.
    refetchInterval: (query) => {
      const status = (query.state.data as ReleasesState | undefined)?.latestRun?.status;
      return status === 'queued' || status === 'in_progress' ? 10_000 : 60_000;
    },
  });
  const changes = useQuery({
    queryKey: ['releases', 'changes'],
    queryFn: getChangesSinceProduction,
    enabled: isTestEnvironment,
  });

  const testResults = useTestResults(releases.data?.candidate.sha ?? null);

  const approve = useMutation({
    mutationFn: approveRelease,
    onSuccess: () => {
      setDialogOpen(false);
      notify.success(t('approved'));
      void queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
    onError: (error) => {
      const code =
        error instanceof ApiError && typeof error.data === 'object' && error.data !== null
          ? (error.data as { code?: string }).code
          : undefined;
      setDialogOpen(false);
      notify.error(
        t(
          code === 'stale_candidate'
            ? 'errors.staleCandidate'
            : code === 'findings_not_acknowledged'
              ? 'errors.findingsChanged'
              : 'errors.approveFailed'
        )
      );
      // Marks may have changed while the dialog was open; ['releases'] covers their query too.
      void queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });

  if (environment === null) return <CircularProgress />;
  if (!isTestEnvironment) {
    return <Alert severity="info">{t('productionOnly')}</Alert>;
  }
  if (releases.isLoading) return <CircularProgress />;
  if (releases.isError || !releases.data) return <Alert severity="error">{t('errors.loadFailed')}</Alert>;

  const state = releases.data;
  const notes = changes.data?.entries ?? [];
  const language = i18n.language.startsWith('en') ? 'en' : 'fi';
  const marks = testResults.data?.marks ?? [];
  const findings = openFindings(notes, marks, language);
  const untested = untestedChanges(notes, marks, language);
  const notTested = notTestedChanges(notes, marks, language);
  const inherited = inheritedOnlyChanges(notes, marks, language);
  // Row counts, as staff-api counts them (#3526): not marks per tester.
  const summary = summarize(notes, marks);
  const holdsPermission = state.blockedReason !== 'no_permission';
  const approverNames = state.approvers.map((a) => a.name).join(', ');
  const blockReasonKey = blockKey(state.blockedReason);

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('title')}
      </Typography>

      <Stack spacing={2}>
        <FailedRunAlert run={state.latestRun} />
        <GithubErrorAlert error={state.githubError} />

        <VersionCards candidate={state.candidate} production={state.production} latestRun={state.latestRun} />

        <TestDataCard />

        <ChangesChecklist
          notes={notes}
          loading={changes.isLoading}
          sha={state.candidate.sha}
          language={language}
          repoUrl={changes.data?.repoUrl ?? null}
        />

        <Card>
          <CardContent>
            {holdsPermission ? (
              <Stack spacing={2} alignItems="flex-start">
                {blockReasonKey && (
                  <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                    {t(blockReasonKey)}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  disabled={!state.canApprove || approve.isPending}
                  onClick={() => {
                    setDialogOpen(true);
                  }}
                >
                  {t('approveButton')}
                </Button>
              </Stack>
            ) : (
              <Typography data-testid="awaiting-approval">
                {approverNames
                  ? t('awaitingApproval', { names: approverNames })
                  : state.restore?.restoredAt
                    ? t('noApprovers', { time: formatDateTime(state.restore.restoredAt) })
                    : t('noApproversNoTime')}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Accordion disableGutters data-testid="release-history">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{t('history.titleWithCount', { count: state.history.length })}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <HistoryList history={state.history} />
          </AccordionDetails>
        </Accordion>
      </Stack>

      {dialogOpen && state.candidate.sha && (
        <ApproveReleaseDialog
          open
          candidate={state.candidate}
          production={state.production}
          changeCount={notes.length}
          openFindings={findings}
          untestedChanges={untested}
          notTestedChanges={notTested}
          inheritedChanges={inherited}
          submitting={approve.isPending}
          onClose={() => {
            setDialogOpen(false);
          }}
          onConfirm={(comment) => {
            approve.mutate({
              sha: state.candidate.sha ?? '',
              ...(comment ? { comment } : {}),
              ...(summary.notOk > 0 || summary.untested > 0
                ? { acknowledged: { openNotOk: summary.notOk, untested: summary.untested } }
                : {}),
            });
          }}
        />
      )}
    </Box>
  );
}

export default ReleaseApprovalPage;
